import { Group16, ListChecked16 } from '@carbon/icons-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SpotlightMessage20 } from 'components/atoms/icons/spotlight-message';
import { ArrElement } from 'utils/arrays';
import {
  SideBarItem,
  SideBarItemIcon,
  SideBarItemName,
  SideBarSection,
  SideBarSectionTitle,
  Sidebar,
} from '../common/side-bar';
import { ProjectTasksListData, TasksToBeShown } from './types';

const SectionTitle = styled(SideBarSectionTitle)`
  display: flex;
  align-items: center;
`;

export interface CpSavedViewsInProjectListProps {
  tasksToBeShown: TasksToBeShown;
  sharedViews: ProjectTasksListData['views'];
  spotlitViews: ProjectTasksListData['spotlitViews'];
  onViewClick: (viewId: number | undefined) => void;
  selectedViewId: number | undefined;
}

const View: React.FC<{
  view: ArrElement<ProjectTasksListData['views']>;
  onClick: () => void;
  isSelected: boolean;
  isSpotlight?: boolean;
}> = ({ view, onClick, isSelected, isSpotlight }) => {
  return (
    <SideBarItem
      data-cy={`cp.project_tasks_list.view.${view?.viewName}`}
      active={isSelected}
      onClick={onClick}
    >
      <SideBarItemIcon>
        {isSpotlight ? <SpotlightMessage20 /> : <Group16 />}
      </SideBarItemIcon>
      <SideBarItemName>{view.viewName}</SideBarItemName>
    </SideBarItem>
  );
};

const AllTasksView: React.FC<{ onClick: () => void; isSelected: boolean }> = ({
  onClick,
  isSelected,
}) => {
  return (
    <SideBarItem
      data-cy="cp.project_tasks_list.all-tasks-view"
      active={isSelected}
      onClick={onClick}
    >
      <SideBarItemIcon>
        <ListChecked16 />
      </SideBarItemIcon>
      <SideBarItemName>
        <FormattedMessage id="menu.label.tasks" />
      </SideBarItemName>
    </SideBarItem>
  );
};

export const CpSavedViewsInProjectList: React.FC<CpSavedViewsInProjectListProps> =
  ({
    tasksToBeShown = TasksToBeShown.AllTasks,
    sharedViews,
    spotlitViews,
    onViewClick,
    selectedViewId,
  }) => {
    return (
      <Sidebar>
        {tasksToBeShown === TasksToBeShown.AllTasks ? (
          <SideBarSection>
            <AllTasksView
              onClick={() => onViewClick(-1)}
              isSelected={selectedViewId === -1}
            />
          </SideBarSection>
        ) : (
          <></>
        )}

        {(spotlitViews?.length ?? 0) > 0 ? (
          <>
            <SideBarSection>
              <SectionTitle>
                <FormattedMessage id="spotlight" />
              </SectionTitle>

              {spotlitViews?.map((view) => (
                <View
                  key={view.viewId}
                  view={view}
                  onClick={() => onViewClick(view.viewId)}
                  isSelected={view.viewId === selectedViewId}
                  isSpotlight
                />
              ))}
            </SideBarSection>
          </>
        ) : (
          <></>
        )}

        {tasksToBeShown === TasksToBeShown.OnlySpotlitSharedViews ||
        (sharedViews?.length ?? 0) === 0 ? (
          <></>
        ) : (
          <SideBarSection>
            <SideBarSectionTitle>
              <FormattedMessage id="views.shared_views.title" />
            </SideBarSectionTitle>
            {sharedViews?.map((view) => (
              <View
                key={view.viewId}
                view={view}
                onClick={() => onViewClick(view.viewId)}
                isSelected={view.viewId === selectedViewId}
              />
            ))}
          </SideBarSection>
        )}
      </Sidebar>
    );
  };
